import React, { useContext, useState } from 'react';

import { cellValueHighLight } from '@src/utils/tableUtils';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Button } from '@uhg-abyss/web/ui/Button';
import { getEEMSURL, getEnvironment } from '@src/env.config';
import { Card } from '@uhg-abyss/web/ui/Card';
import CryptoJS from 'crypto-js';
import { encdata } from '@src/constants';

import { useLazyGetReportsQuery } from '@src/store/services/cdb/onlineServices';
import { useLazyGetGpsSearchReportsQuery } from '@src/store/services/gps/onlineServices';
import { FileContext } from '@src/routes/Routes';
import { Table } from '@src/common/Table';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';

const filters = [
  { label: 'Employee ID', value: 'employeeId' },
  { label: 'Severity', value: 'severityCode' },
  { label: 'Name', value: 'name' },
  { label: 'Relationship Code', value: 'rel' },
  { label: 'Error Name', value: 'warningName' },
  { label: 'Status', value: 'stsTxt' },
  { label: 'Type', value: 'type' },
  { label: 'Data Field', value: 'dataField' },
];

export const FieldChangeSummaryChild = ({
  fileId,
  fieldCode,
  isChild,
  setId,
}) => {
  const router = useRouter();
  const { service, dataFieldValue } = router.getRouteParams();

  const [primaryFilter, setPrimaryFilter] = useState('employeeId');
  const [secondaryFilter, setSecondaryFilter] = useState('rel');
  const [primarySort, setPrimarySort] = useState('employeeId');
  const [secondarySort, setSecondarySort] = useState('rel');
  const [isTableLoading, setIsTableLoading] = useState(false);

  const { fileData } = useContext(FileContext);

  const handleSubmit = (e) => {
    e.preventDefault();
    setPrimarySort(primaryFilter);
    setSecondarySort(secondaryFilter);
  };

  const columns = React.useMemo(() => {
    return [
      {
        Header: 'Severity',
        accessor: 'severityCode',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 90,
        width: 90,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },

      {
        Header: 'Employee ID',
        accessor: 'employeeId',
        canToggleVisibilty: true,
        isHiddenByDefault: service === 'gps' ? true : false,
        minWidth: 90,
        width: 90,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },

      {
        Header: 'Medicare ID',
        accessor: 'medicareId',
        canToggleVisibilty: false,
        isHiddenByDefault: service === 'gps' ? false : true,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Family ID',
        accessor: 'familyId',
        canToggleVisibilty: false,
        isHiddenByDefault: service === 'gps' ? false : true,
      },
      {
        Header: 'Name',
        accessor: 'name',
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Rel',
        accessor: 'rel',
        canToggleVisibilty: true,
        isHiddenByDefault: service === 'gps' ? true : false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Coverage Type',
        accessor: 'covType',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 120,
        width: 120,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Status',
        accessor: service === 'gps' ? 'status' : 'stsTxt',
        minWidth: 120,
        width: 120,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Type',
        accessor: 'type',
        minWidth: 120,
        width: 120,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Transaction Source',
        accessor: 'transactionSource',
        minWidth: 75,
        width: 75,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
        canToggleVisibilty: service !== 'gps',
        isHiddenByDefault: service === 'gps' ? true : false,
      },
      {
        Header: 'Error Name',
        accessor: service === 'gps' ? 'errorName' : 'warningName',
        minWidth: 100,
        width: 100,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Data Field',
        accessor: 'dataField',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
        width: 100,
      },
      {
        Header: 'Data Prior To Processing',
        accessor: 'dataPriortoProcessing',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Data After Processing',
        accessor: 'dataAfterProcessing',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: 'Detail View',
        accessor: 'detailView',
        enableSorting: false,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 90,
        width: 90,
        Cell: ({ row }) => {
          const nameSplit = row?.original?.name.split(',');
          const firstName = nameSplit?.[1]?.trim();
          const lastName = nameSplit?.[0]?.trim();
          const typeValue = row?.original?.multiTransId?.trim();
          const value =
            service !== 'gps' &&
            fileData?.multilevelSubmitter &&
            typeValue?.length > 0
              ? 'View'
              : 'Detail View';

          const link =
            service === 'gps'
              ? `/home/gps-detail-view/`
              : fileData?.multilevelSubmitter && typeValue?.length > 0
              ? `/home/multi-level-view/`
              : `/home/detail-view/`;

          const linkData = {
            fileID: fileData?.fileID || fileData.fileId,
            transSetId: fileData?.transSetId,
            medicareId: row?.original?.medicareId,
            firstName: firstName,
            lastName: lastName.trim(),
            processingDt: fileData?.processingDt,
            customerName: fileData?.customerName,
            mbrTicRelCd: row?.original?.mbrTicRelCd,
            transId: row?.original?.transId,
            subscrId: row?.original?.subscrId,
            sbmtGrpName: fileData?.sbmtGrpName,
            relationship: row?.original?.rel,
            cescustnbr: fileData?.cescustnbr,
            sbmtGrpId: fileData?.sbmtGrpId,
            reportType: 'FC',
            empId: row?.original?.employeeId,

            // Add other parameters as needed
          };

          const encryptedData = CryptoJS.AES.encrypt(
            JSON.stringify(linkData),
            encdata
          ).toString();

          const newUrl = `${link}${encodeURIComponent(encryptedData)}`;

          return cellValueHighLight({
            value: (
              <Flex
                css={{
                  gap: '10px',
                }}
              >
                <Link
                  size="$sm"
                  target="_blank"
                  href={newUrl}
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(newUrl, 'DetailViewTab');
                  }}
                >
                  <Button
                    variant="outline"
                    size="$sm"
                    css={{
                      fontSize: '12px',
                    }}
                  >
                    <strong>{value}</strong>
                  </Button>
                </Link>
              </Flex>
            ),
            fileData,
            typeValue,
          });
        },
      },
    ];
  }, []);

  return service === 'gps' ? (
    <React.Fragment>
      <Card>
        <div
          style={{
            backgroundColor:
              'var(--abyss-colors-web-semantic-color-surface-container-primary)',
            color:
              'var(--abyss-colors-web-semantic-color-text-content-primaryAlt)',
            fontSize: '18px',
            padding: '24px',
            borderTopLeftRadius: '3px',
            borderTopRightRadius: '3px',
            fontWeight: 'bold',
          }}
        >
          {`Field Change Summary  -  ${dataFieldValue}`}
        </div>
        <div style={{ padding: '16px' }}>
          <Table
            columns={columns}
            dataKey="fileListTable"
            fetch={useLazyGetGpsSearchReportsQuery}
            requestParams={{
              ...router.getRouteParams(),
              ...fileData,
              allDate: 'false',
            }}
            showGlobalFilter={true}
          />
        </div>
      </Card>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <Card>
        <div
          style={{
            backgroundColor:
              'var(--abyss-colors-web-semantic-color-surface-container-primary)',
            color:
              'var(--abyss-colors-web-semantic-color-text-content-primaryAlt)',
            fontSize: '18px',
            padding: '24px',
            borderTopLeftRadius: '3px',
            borderTopRightRadius: '3px',
            fontWeight: 'bold',
          }}
        >
          {`Field Change Summary  -  ${dataFieldValue}`}
        </div>
        <div style={{ padding: '16px' }}>
          <Table
            columns={columns}
            dataKey="fileListTable"
            fetch={useLazyGetReportsQuery}
            requestParams={{
              ...router.getRouteParams(),
              ...fileData,
              allDate: 'false',
              primarySort,
              secondarySort,
            }}
            showGlobalFilter={true}
            onLoadingChange={setIsTableLoading}
            customHeaderSection={
              <form onSubmit={handleSubmit}>
                <Layout.Group
                  css={{
                    gap: '20px',
                    alignItems: 'flex-end',
                    '.abyss-select-input-root': {
                      minWidth: '288px',
                    },
                  }}
                >
                  <SelectInput
                    label="Primary Sort"
                    value={primaryFilter}
                    onChange={setPrimaryFilter}
                    options={filters}
                  />
                  <SelectInput
                    label="Secondary Sort"
                    value={secondaryFilter}
                    onChange={setSecondaryFilter}
                    options={filters}
                  />

                  <Box
                    css={{
                      'abyss-box-root': {
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0',
                        background: 'transparent',
                      },
                    }}
                  >
                    <Button
                      size="$sm"
                      type="submit"
                      isDisabled={isTableLoading}
                    >
                      Submit
                    </Button>
                    <Button
                      size="$sm"
                      variant="filled"
                      color="destructive"
                      isDisabled={isTableLoading}
                      style={{ marginLeft: '8px' }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Layout.Group>
              </form>
            }
          />
        </div>
      </Card>
    </React.Fragment>
  );
};
